// Copyright 2021 The Casdoor Authors. All Rights Reserved.
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//      http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import React from "react";
import {Link} from "react-router-dom";
import {Button, Modal, Popconfirm, Select, Switch, Table} from "antd";
import * as Setting from "../../conf/Setting";
import * as RoleBackend from "../../services/RoleBackend";
import * as OrganizationBackend from "../../services/OrganizationBackend";
import i18next from "i18next";
import BaseListPage from "../../components/basic/BaseListPage";
import {v4 as uuidv4} from "uuid";
import moment from "moment";

class RoleListPage extends BaseListPage {
  constructor(props) {
    super(props);
    this.state = {
      ...this.state,
      modalVisible: false,
      isStaticRole: false,
      selectedOrganization: "",
      selectedOrganName: "",
      confirmLoading: false,
      organizations: [],
      forbiddenChecked: false,
      forbiddenRole: null,
      forbiddenModalVisible: false,
      confirmForbiddenLoading: false,
    };
  }

  newRole(owner, organName, isStatic) {
    const randomName = Setting.getRandomName();
    return {
      owner: owner, // 组织uid
      uid: uuidv4(), // uuid
      displayName: `New Role - ${randomName}`,
      createdTime: moment().format("yyyy-MM-DD HH:mm:ss"),
      updatedTime: moment().format("yyyy-MM-DD HH:mm:ss"),
      domains: ["admin"], // 域，默认admin
      users: [],  // 所包含的用户
      isEnabled: true, // 启用禁用，true启用
      userNum: 0, // 属于该组织的用户数量,新建角色时为0，后续增加用户时会进行增加
      isStaticRole: isStatic, // 是否是静态角色true 静态角色 false 动态角色
      organName: organName,
      describe: "", // 描述信息
    };
  }

  UNSAFE_componentWillMount() {
    super.UNSAFE_componentWillMount();
    this.getOrganizations();
  }

  getOrganizations() {
    OrganizationBackend.getOrganizations(Setting.Admin)
      .then((res) => {
        if (res.msg === undefined) {
          const org = res.filter(organization => !Setting.isAdminOrganization(organization.uid)).reverse();
          this.setState({
            organizations: org,
          });
          this.setState({selectedOrganization: org.length > 0 ? org[0].uid : "", selectedOrganName: org.length > 0 ? org[0].displayName : ""});
        } else {
          this.setState({
            organizations: [],
          });
        }
      });
  }

  addStaticRole() {
    this.setState({modalVisible: true, isStaticRole: true});
  }

  addDynamicRole() {
    this.setState({modalVisible: true, isStaticRole: false});
  }

  addRole(isStatic = false) {
    const newRole = this.newRole(this.state.selectedOrganization, this.state.selectedOrganName, isStatic);
    this.setState({confirmLoading: true});
    RoleBackend.addRole(newRole)
      .then((res) => {
        this.setState({confirmLoading: false});
        if (res.status === "ok") {
          this.props.history.push({pathname: `/roles/${newRole.owner}/${newRole.uid}`, mode: "add"});
        } else {
          Setting.showResponseMessage(res.msg);
        }
      }
      )
      .catch(error => {
        Setting.showMessage("error", `Role failed to add: ${error}`);
        this.setState({confirmLoading: false});
      });
  }

  onAddRole() {
    if (this.state.selectedOrganization === "") {
      Setting.showMessage("error", i18next.t("role:Please select an organization for role"));
      return;
    }

    this.addRole(this.state.isStaticRole);
  }

  deleteRole(i) {
    RoleBackend.deleteRole(this.state.data[i])
      .then((res) => {
        Setting.showMessage("success", i18next.t("role:Role deleted successfully"));
        this.handleDelete();
      }
      )
      .catch(error => {
        Setting.showMessage("error", `Role failed to delete: ${error}`);
      });
  }

  handleClickForbiddenSwitch(role, checked) {
    if (!checked) {
      this.setState({
        forbiddenChecked: checked,
        forbiddenRole: role,
        forbiddenModalVisible: true,
      });
    } else {
      const newRole = {...role};
      newRole.isEnabled = checked;
      RoleBackend.updateRole(newRole.owner, newRole.uid, newRole)
        .then(res => {
          if (res.status === "ok") {
            this.fetch({pagination: this.state.pagination, searchText: this.state.searchText, ...this.state.filters});
          } else {
            Setting.showResponseMessage(res.msg);
          }
        })
        .catch(error => {
          Setting.showResponseMessage(error);
        });
    }
  }

  handleForbiddenRole() {
    this.setState({
      confirmForbiddenLoading: true,
    });
    const newRole = {...this.state.forbiddenRole};
    newRole.isEnabled = this.state.forbiddenChecked;
    RoleBackend.updateRole(newRole.owner, newRole.uid, newRole)
      .then(res => {
        this.setState({
          confirmForbiddenLoading: false,
        });
        if (res.status === "ok") {
          this.setState({
            forbiddenModalVisible: false,
          });
          this.fetch({pagination: this.state.pagination, searchText: this.state.searchText, ...this.state.filters});
        } else {
          Setting.showResponseMessage(res.msg);
        }
      })
      .catch(error => {
        this.setState({
          confirmForbiddenLoading: false,
        });
        Setting.showResponseMessage(error);
      });
  }

  renderTable(roles) {
    const columns = [
      {
        title: i18next.t("general:Organization"),
        dataIndex: "organName",
        key: "organName",
        width: "160px",
        ...this.getColumnSearchProps("organ_name"),
        // render: (text, record, index) => {
        //   return (
        //     <Link to={`/organizations/${text}`}>
        //       {text}
        //     </Link>
        //   );
        // },
      },
      {
        title: i18next.t("role:Role name"),
        dataIndex: "displayName",
        key: "displayName",
        fixed: "left",
        ...this.getColumnSearchProps("displayName"),
        render: (text, record, index) => {
          return (
            <Link to={`/roles/${record.owner}/${record.uid}`}>
              {text}
            </Link>
          );
        },
      },
      {
        title: i18next.t("role:User quantity"),
        dataIndex: "userNum",
        key: "userNum",
        width: "140px",
        ellipsis: true,
      },
      {
        title: i18next.t("role:Type"),
        dataIndex: "isStaticRole",
        key: "isStaticRole",
        width: "140px",
        filterMultiple: false,
        filters: [
          {text: i18next.t("role:Static role"), value: 1},
          {text: i18next.t("role:Dynamic role"), value: 0},
        ],
        render: (text, record, index) => {
          return text === true ? i18next.t("role:Static role") : i18next.t("role:Dynamic role");
        },
      },
      {
        title: i18next.t("general:Created time"),
        dataIndex: "createdTime",
        key: "createdTime",
        width: "160px",
        sorter: true,
        render: (text, record, index) => {
          return Setting.getFormattedDate(text);
        },
      },
      {
        title: i18next.t("role:Enabled state"),
        dataIndex: "isEnabled",
        key: "isEnabled",
        width: "120px",
        render: (text, record, index) => {
          return (
            <Switch
              checkedChildren="ON"
              unCheckedChildren="OFF"
              checked={text}
              onClick={(checked) => this.handleClickForbiddenSwitch(record, checked)} />
          );
        },
      },
      {
        title: i18next.t("general:Action"),
        dataIndex: "",
        key: "op",
        width: "170px",
        fixed: (Setting.isMobile()) ? "false" : "right",
        render: (text, record, index) => {
          return (
            <div>
              <Button style={{marginTop: "10px", marginBottom: "10px", marginRight: "10px"}} type="primary" onClick={() => this.props.history.push(`/roles/${record.owner}/${record.uid}`)}>{i18next.t("general:Edit")}</Button>
              <Popconfirm
                title={`${i18next.t("role:Sure to delete role")}: ${record.displayName} ?`}
                onConfirm={() => this.deleteRole(index)}
              >
                <Button style={{marginBottom: "10px"}} type="danger">{i18next.t("general:Delete")}</Button>
              </Popconfirm>
            </div>
          );
        },
      },
    ];

    const paginationProps = {
      total: this.state.pagination.total,
      showQuickJumper: true,
      showSizeChanger: true,
      showTotal: () => i18next.t("general:{total} in total").replace("{total}", this.state.pagination.total),
    };

    return (
      <div>
        <Table scroll={{x: "max-content"}} columns={columns} dataSource={roles} rowKey="name" size="middle" bordered pagination={paginationProps}
          title={() => (
            <div>
              {i18next.t("general:Roles")}&nbsp;&nbsp;&nbsp;&nbsp;
              <Button type="primary" size="small" onClick={this.addDynamicRole.bind(this)}>{i18next.t("role:New dynamic role")}</Button>
              <Button style={{marginLeft: 10}} type="primary" size="small" onClick={this.addStaticRole.bind(this)}>{i18next.t("role:New static role")}</Button>
            </div>
          )}
          loading={this.state.loading}
          onChange={this.handleTableChange}
        />
        {
          this.state?.modalVisible ?
            <Modal
              title={i18next.t("role:New Role")}
              visible={this.state.modalVisible}
              okText={i18next.t("general:Confirm")}
              cancelText={i18next.t("general:Cancel")}
              confirmLoading={this.state.confirmLoading}
              onOk={() => this.onAddRole()}
              onCancel={() => {this.setState({modalVisible: false});}}
            >
              <p>{i18next.t("role:Please select an organization for role")}</p>
              <Select virtual={false} style={{width: "100%"}} value={this.state.selectedOrganization} onChange={((value, option) => {
                this.setState({selectedOrganization: value, selectedOrganName: option.children});
              })}>
                {
                  this.state.organizations.map((organization, index) => <Select.Option key={index} value={organization.uid}>{organization.displayName}</Select.Option>)
                }
              </Select>
            </Modal>
            : null
        }
        <Modal
          title={i18next.t("role:Confirm info")}
          visible={this.state.forbiddenModalVisible}
          okText={i18next.t("general:Confirm")}
          cancelText={i18next.t("general:Cancel")}
          confirmLoading={this.state.confirmForbiddenLoading}
          onOk={() => {this.handleForbiddenRole();}}
          onCancel={() => {this.setState({forbiddenModalVisible: false});}}
        >
          <p>{i18next.t("role:Sure to disable this role")}</p>
        </Modal>
      </div>
    );
  }

  fetch = (params = {}) => {
    let field = params.searchedColumn, value = params.searchText;
    const sortField = params.sortField, sortOrder = params.sortOrder;
    if (params.isStaticRole !== undefined && params.isStaticRole !== null) {
      field = "is_static_role";
      value = params.isStaticRole;
    }
    this.setState({loading: true});
    RoleBackend.getRoles("", params.pagination.current, params.pagination.pageSize, field, value, sortField, sortOrder)
      .then((res) => {
        if (res.status === "ok") {
          this.setState({
            loading: false,
            data: res.data,
            pagination: {
              ...params.pagination,
              total: res.data2,
            },
            searchText: params.searchText,
            searchedColumn: params.searchedColumn,
          });
        }
      });
  };
}

export default RoleListPage;
